import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';
import { 
  fetchEmployeesWithSubmissions, 
  sendReminders, 
  clearAdminErrors 
} from '../../../../store/slices/gameTestingSlice';
import './GameTestingReminderNotificationModal.scss';

const GameTestingReminderNotificationModal = () => {
  const dispatch = useDispatch();
  const { 
    employees,
    employeesLoading,
    employeesWithSubmissions,
    submissionsLoading,
    submissionsError,
    reminderSending,
    reminderError
  } = useSelector(state => state.gameTesting);
  const { user } = useSelector(state => state.auth);
  
  const [show, setShow] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [reminderSuccess, setReminderSuccess] = useState(false);
  
  useEffect(() => {
    if (show) {
      dispatch(fetchEmployeesWithSubmissions());
    }
  }, [show, dispatch]);
  
  useEffect(() => {
    if (reminderSuccess) {
      // Auto-hide success message after 3 seconds
      const timer = setTimeout(() => {
        setReminderSuccess(false);
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [reminderSuccess]);
  
  const handleClose = () => {
    setShow(false);
    setSelectedUsers([]);
    setSelectAll(false);
    setReminderSuccess(false);
    dispatch(clearAdminErrors());
  };
  
  const handleShow = () => setShow(true);
  
  const handleSelectAll = (e) => {
    setSelectAll(e.target.checked);
    setSelectedUsers(e.target.checked ? 
      employees.map(user => user.email || user.schoolEmail) : 
      []
    );
  };
  
  const handleUserSelect = (email) => {
    setSelectedUsers(prev => {
      const newSelection = prev.includes(email)
        ? prev.filter(e => e !== email)
        : [...prev, email];
      
      // Update selectAll state based on if all employees are selected
      setSelectAll(newSelection.length === employees.length);
      return newSelection;
    });
  };
  
  const getSubmissionStatus = (email) => {
    const submission = employeesWithSubmissions[email];
    
    if (!submission) {
      return (
        <div className="submission-status">
          <span className="status-badge danger">No Submission This Week</span>
          <div className="status-details">
            <span>Pending Reports: 0</span>
          </div>
        </div>
      );
    }
    
    return (
      <div className="submission-status">
        <div className="badges-container">
          <span className={`status-badge ${submission.submissions >= 2 ? 'success' : 'warning'}`}>
            {submission.submissions} Submission{submission.submissions !== 1 ? 's' : ''}
          </span>
          {submission.pendingReports > 0 && (
            <span className="status-badge danger">
              {submission.pendingReports} Pending
            </span>
          )}
        </div>
        <div className="status-details">
          <span>Last: {format(new Date(), "MMM d, yyyy")}</span>
          <span>Games: {submission.reportIds?.length || 0}</span>
        </div>
      </div>
    );
  };
  
  const handleSendReminders = async () => {
    if (selectedUsers.length === 0) {
      alert('Please select at least one user');
      return;
    }
    
    try {
      await dispatch(sendReminders({
        selectedUsers,
        adminUser: user
      })).unwrap();
      
      setReminderSuccess(true);
      setSelectedUsers([]);
      setSelectAll(false);
      
      // Refresh the employee data
      dispatch(fetchEmployeesWithSubmissions());
    } catch (error) {
      console.error('Error sending reminders:', error);
    }
  };
  
  const handleRetry = () => {
    dispatch(clearAdminErrors());
    dispatch(fetchEmployeesWithSubmissions());
  };
  
  const currentWeekStart = format(new Date(), 'MMM d, yyyy');
  const currentWeekEnd = format(new Date(Date.now() + 6 * 86400000), 'MMM d, yyyy');
  
  return (
    <>
      <button 
        onClick={handleShow} 
        className="reminder-button"
        disabled={reminderSending}
      >
        {reminderSending ? (
          <>
            <span className="loading-spinner-small"></span>
            Sending...
          </>
        ) : (
          'Send Weekly Reminders'
        )}
      </button>
      
      {show && (
        <div className="modal-overlay">
          <div className="modal-content reminder-modal">
            <div className="modal-header">
              <h3>Send Game Testing Reminders</h3>
              <button 
                className="close-button"
                onClick={handleClose}
                disabled={reminderSending}
              >
                &times;
              </button>
            </div>
            
            <div className="modal-body">
              <div className="reminder-info">
                <p>
                  Select users to send game testing reminder notifications for the week of:
                  <strong> {currentWeekStart} - {currentWeekEnd}</strong>
                </p>
                <p className="note">
                  Note: This will create a new empty testing report for each selected user 
                  and send them an email notification.
                </p>
              </div>
              
              {reminderSuccess && (
                <div className="success-message">
                  Reminders sent successfully! The selected employees will receive their assignments.
                </div>
              )}
              
              {reminderError && (
                <div className="error-message">
                  Error sending reminders: {reminderError}
                  <button 
                    className="retry-button"
                    onClick={() => dispatch(clearAdminErrors())}
                  >
                    Dismiss
                  </button>
                </div>
              )}
              
              {submissionsError && (
                <div className="error-message">
                  Error loading employee data: {submissionsError}
                  <button 
                    className="retry-button"
                    onClick={handleRetry}
                  >
                    Retry
                  </button>
                </div>
              )}
              
              {(employeesLoading || submissionsLoading) ? (
                <div className="loading-container">
                  <div className="loading-spinner"></div>
                  <p>Loading employee data...</p>
                </div>
              ) : (
                <>
                  <div className="select-all-container">
                    <label>
                      <input 
                        type="checkbox" 
                        checked={selectAll}
                        onChange={handleSelectAll}
                        disabled={reminderSending}
                      />
                      Select All Users ({employees.length})
                    </label>
                  </div>
                  
                  <div className="users-table-container">
                    <table className="users-table">
                      <thead>
                        <tr>
                          <th className="select-column"></th>
                          <th>User</th>
                          <th>This Week's Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {employees.length > 0 ? (
                          employees.map((user, index) => {
                            const email = user.email || user.schoolEmail;
                            
                            return (
                              <tr key={email ? `${email}-${index}` : `user-${user.id}-${index}`}>
                                <td className="select-column">
                                  <input 
                                    type="checkbox"
                                    checked={selectedUsers.includes(email)}
                                    onChange={() => handleUserSelect(email)}
                                    disabled={reminderSending}
                                  />
                                </td>
                                <td>
                                  <div className="user-info">
                                    <span className="user-name">{user.name || user.displayName || email.split('@')[0]}</span>
                                    <span className="user-email">{email}</span>
                                  </div>
                                </td>
                                <td>{getSubmissionStatus(email)}</td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan="3" style={{ textAlign: 'center', padding: '20px' }}>
                              No employees found.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
            
            <div className="modal-footer">
              <button 
                className="cancel-button"
                onClick={handleClose}
                disabled={reminderSending}
              >
                Cancel
              </button>
              <button 
                className="send-button"
                onClick={handleSendReminders}
                disabled={reminderSending || selectedUsers.length === 0}
              >
                {reminderSending ? (
                  <>
                    <span className="spinner"></span>
                    Sending...
                  </>
                ) : (
                  `Send Reminders (${selectedUsers.length})`
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default GameTestingReminderNotificationModal; 